import './App.css';
import { SiteNav } from './SideNav/SiteNav.tsx';
import { OneHundredChart } from './Charts/OneHundredChart.tsx';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ProcessUrl } from './MathFactsGenerator/UrlProcessor/ProcessUrl.tsx';
import { DefaultSettings } from './MathFactsGenerator/Models.ts';
import { MultiplicationChart } from './Charts/MultiplicationChart.tsx';
import { LandingPage } from './Landing/LandingPage.tsx';
import { FlashCardsPage } from './FlashCards/FlashCardsPage.tsx';
import { QuizPage } from './FlashCards/Quiz/QuizPage.tsx';
import { DropGamePage } from './FlashCards/Game/DropGamePage.tsx';
import { ChartGamePage } from './FlashCards/Game/ChartGamePage.tsx';
import { ProductGamePage } from './FlashCards/Game/ProductGamePage.tsx';
import { MunchGamePage } from './FlashCards/Game/MunchGamePage.tsx';
import { ColliePage } from './FlashCards/Game/ColliePage.tsx';
import { SoccerGamePage } from './FlashCards/Game/SoccerGamePage.tsx';
import { PlateGamePage } from './FlashCards/Game/PlateGamePage.tsx';
import { FootballGamePage } from './FlashCards/Game/FootballGamePage.tsx';
import { RunnerGamePage } from './FlashCards/Game/RunnerGamePage.tsx';
import { MergeGamePage } from './FlashCards/Game/MergeGamePage.tsx';
import { ShopGamePage } from './FlashCards/Game/ShopGamePage.tsx';
import { PictureGamePage } from './FlashCards/Game/PictureGamePage.tsx';
import { CrosswordGamePage } from './FlashCards/Game/CrosswordGamePage.tsx';
import { DailyPuzzlePage } from './DailyPuzzle/DailyPuzzlePage.tsx';
import { CardGamePage } from './CardGames/CardGamePage.tsx';
import { NotFound } from './NotFound/NotFound.tsx';
import { SiteFooter } from './Shared/SiteFooter.tsx';
import { landingRoutes, hubRoutes, packRoutes, chartRoutes, flashCardRoutes, quizRoutes, dailyPuzzleRoutes, cardGameRoutes, contentRoutes, indexRoutes } from './Shared/Seo/routeSeo.ts';
import { GradePage } from './Landing/GradePage.tsx';
import { PackPage } from './Landing/PackPage.tsx';
import { ContentPage } from './Shared/ContentPage.tsx';
import { AllWorksheets } from './Shared/AllWorksheets.tsx';
import { LocaleProvider } from './Shared/i18n/LocaleContext.tsx';
import { ConsentBanner } from './Shared/Consent/ConsentBanner.tsx';

const chartComponents = {
  OneHundredChart: <OneHundredChart/>,
  MultiplicationChart: <MultiplicationChart/>,
};

function App() {

  const urlQuery = window.location.search;
  let initialSettings = DefaultSettings;
  if (urlQuery) {
    initialSettings = ProcessUrl(urlQuery);
  }

  // All MathFactsGenerator routes are driven by the manifest (routeSeo.json).
  // Adding a new operation landing page is a data-only change in that file.
  const landings = landingRoutes();
  // Grade hubs - a page of skill tiles, one per grade (ADR 0062).
  const hubs = hubRoutes();
  // Substitute packs - a page that builds one PDF of several sheets (ADR 0083).
  const packs = packRoutes();
  // Chart pages come from the same manifest so they get titles, descriptions,
  // canonicals and sitemap entries like every other page.
  const charts = chartRoutes();
  // On-screen flash-card pages: same manifest, one route per operation. The
  // operation travels as data on the entry, so a single component backs them all.
  const flashCards = flashCardRoutes();
  // Typed-answer quiz pages: same shape again, one route per operation.
  const quizzes = quizRoutes();
  // The daily puzzle page - its own discriminator, not a quiz (ADR 0127).
  const dailyPuzzles = dailyPuzzleRoutes();
  // Printable card game pages - their own discriminator (ADR 0135).
  const cardGames = cardGameRoutes();
  // Prose pages - About, Privacy. Same manifest, no tool attached.
  const contentPages = contentRoutes();
  // The link-graph hub (ADR 0027) - one per locale since ADR 0051 §8; each
  // slug lives in the manifest like the rest.
  const indexes = indexRoutes();

  return (
    <div className="App">
      <div>
        <Router>
          {/* ADR 0051 §6: the interface locale is resolved from a `uiLang`
              query key, entirely outside worksheet state - it never touches
              ISettingsProps, generateUrl or ProcessUrl. This is a review
              affordance for part 2, not the shipped switcher; part 4 makes
              the route itself the source. */}
          <LocaleProvider>
          <SiteNav/>
          {/* The page itself, between the site header and the footer: the
              `main` landmark a screen reader jumps to. scripts/prerender.js
              wraps the same two things the same way, so the static page and
              the hydrated one have the same landmarks. */}
          <main id="main">
          <Routes>
            {/* Home: shared URL-state entry point. Rendered through the landing
                template so its title and copy come from the manifest - the same
                values scripts/prerender.js bakes into the static HTML - rather
                than from a title the generator builds at runtime. */}
            <Route exact path="" element={
              <LandingPage slug="/" initialSettings={initialSettings}/>
            }/>

            {/* Canonical SEO landing pages (keyword slugs) — from manifest */}
            {landings.map((route) => (
              <Route key={route.slug} path={route.slug} element={
                <LandingPage slug={route.slug}/>
              }/>
            ))}

            {/* Grade hubs — from manifest */}
            {hubs.map((route) => (
              <Route key={route.slug} path={route.slug} element={
                <GradePage slug={route.slug}/>
              }/>
            ))}

            {/* Substitute packs — from manifest */}
            {packs.map((route) => (
              <Route key={route.slug} path={route.slug} element={
                <PackPage slug={route.slug}/>
              }/>
            ))}

            {/* Chart pages — from manifest */}
            {charts.map((route) => (
              <Route key={route.slug} path={route.slug} element={chartComponents[route.chart]}/>
            ))}

            {/* Flash-card pages — from manifest */}
            {flashCards.map((route) => (
              <Route key={route.slug} path={route.slug} element={
                <FlashCardsPage slug={route.slug} operation={route.flashCard}/>
              }/>
            ))}

            {/* Quiz pages — from manifest. A quiz route carrying `game` is the
                drop, chart, product, munch, collie, soccer, plates, football or
                runner, merge, shop, picture or crossword game instead (ADRs 0053-0057, 0059, 0060,
                0103, 0124, 0128, 0131, 0133, 0134). */}
            {quizzes.map((route) => (
              <Route key={route.slug} path={route.slug} element={
                route.game === 'picture'
                  ? <PictureGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'crossword'
                  ? <CrosswordGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'shop'
                  ? <ShopGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'merge'
                  ? <MergeGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'runner'
                  ? <RunnerGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'football'
                  ? <FootballGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'plates'
                  ? <PlateGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'soccer'
                  ? <SoccerGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'collie'
                  ? <ColliePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'munch'
                  ? <MunchGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'product'
                  ? <ProductGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'chart'
                  ? <ChartGamePage slug={route.slug} operation={route.quiz}/>
                  : route.game === 'drop'
                  ? <DropGamePage slug={route.slug} operation={route.quiz}/>
                  : <QuizPage slug={route.slug} operation={route.quiz}/>
              }/>
            ))}

            {/* Daily puzzle pages — from manifest (ADR 0127) */}
            {dailyPuzzles.map((route) => (
              <Route key={route.slug} path={route.slug} element={
                <DailyPuzzlePage slug={route.slug}/>
              }/>
            ))}

            {/* Printable card game pages — from manifest (ADR 0135) */}
            {cardGames.map((route) => (
              <Route key={route.slug} path={route.slug} element={
                <CardGamePage slug={route.slug} id={route.cardGame}/>
              }/>
            ))}

            {/* The all-pages index, one per locale — from manifest */}
            {indexes.map((route) => (
              <Route key={route.slug} path={route.slug} element={
                <AllWorksheets slug={route.slug}/>
              }/>
            ))}

            {/* Prose pages — from manifest */}
            {contentPages.map((route) => (
              <Route key={route.slug} path={route.slug} element={
                <ContentPage slug={route.slug}/>
              }/>
            ))}

            {/* Legacy path → canonical slug redirects — from manifest.
                Azure also answers these with a 301 (staticwebapp.config.json);
                these keep in-app navigation working. */}
            {[...landings, ...charts].flatMap((route) =>
              (route.legacyPaths ?? []).map((legacy) => (
                <Route key={legacy} path={legacy} element={
                  <Navigate to={route.slug} replace/>
                }/>
              ))
            )}

            {/* Anything else: real content and a noindex, not a blank 200 */}
            <Route path="*" element={<NotFound/>}/>
          </Routes>
          </main>
          <SiteFooter/>
          {/* Analytics consent, ADR 0025 amendment 2. Renders only for a
              European visitor who has not answered; everyone else gets null.
              Last in the tree because it is fixed-position chrome and must
              never push the h1 or the tool down the page. */}
          <ConsentBanner/>
          </LocaleProvider>
        </Router>
      </div>
    </div>
  );
}

export default App;
